import { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Pause, Play, Volume2, VolumeX } from "lucide-react";
import EmailCapture from "./EmailCapture";
import { EASE } from "./Reveal";
import { AUDIO_PREVIEW_SRC, STREAMING_LINKS } from "../config";

const HERO_SOLID = ["GRACEFUL", "BY CHOICE."];
const HERO_LINES = [
    ...HERO_SOLID.map((l) => ({ l, stroke: false })),
    { l: "UNBREAKABLE", stroke: true },
    { l: "BY NATURE.", stroke: true },
];

function fmt(s) {
    const v = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
}

export default function Hero() {
    const reduce = useReducedMotion();
    const sectionRef = useRef(null);
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [started, setStarted] = useState(false);
    const [muted, setMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(30);

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
        } else {
            audio.play().catch(() => setPlaying(false));
        }
    };

    const toggleMute = () => {
        setMuted((m) => {
            const next = !m;
            if (audioRef.current) audioRef.current.muted = next;
            return next;
        });
    };

    const seek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        const t = Number(e.target.value);
        audio.currentTime = t;
        setProgress(t);
    };

    return (
        <section ref={sectionRef} id="top" className="relative bg-garden">
            {!reduce && (
                <motion.div
                    aria-hidden="true"
                    className="fixed inset-0 z-[95] bg-garden flex items-center justify-center pointer-events-none"
                    initial={{ y: 0 }}
                    animate={{ y: "-100%" }}
                    transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
                >
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.15, duration: 0.3 }}
                        className="font-display font-extrabold uppercase tracking-[0.35em] text-blush text-sm"
                    >
                        Mashi J
                    </motion.span>
                </motion.div>
            )}

            <div className="grid lg:grid-cols-2 min-h-screen max-w-[1600px] mx-auto" style={{ minHeight: "100svh" }}>
                <div className="relative z-10 flex flex-col justify-center px-5 md:px-10 pt-28 pb-16 md:pt-24 md:pb-20 order-1">
                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
                        className="flex items-center gap-3 text-[11px] md:text-sm uppercase tracking-[0.25em] text-blush/85 mb-5 md:mb-7"
                    >
                        <span className="h-2 w-2 bg-honeysuckle" aria-hidden="true" />
                        New single out now
                    </motion.p>

                    <h1 className="font-display font-extrabold uppercase text-blush leading-[0.88] tracking-[-0.03em] text-[clamp(2.9rem,7.2vw,6.2rem)]">
                        {HERO_LINES.map((line, i) => (
                            <span key={line.l} className="block overflow-hidden">
                                <motion.span
                                    className={`block ${line.stroke ? "text-stroke" : ""}`}
                                    initial={reduce ? false : { y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1, delay: 0.65 + i * 0.12, ease: EASE }}
                                >
                                    {line.l}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                        className="mt-5 md:mt-7 text-base md:text-xl font-light text-blush/90 max-w-md"
                    >
                        Neo-Soul, Jazz, R&amp;B, House, and Blues. Five Worlds Brought Together By One Voice.
                    </motion.p>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                        className="mt-8"
                    >
                        <div data-testid="hero-player" className="border border-blush/25 bg-garden/60 p-4 md:p-5 flex items-center gap-4 md:gap-5 max-w-xl">
                            <button
                                type="button"
                                data-testid="player-play-btn"
                                onClick={togglePlay}
                                aria-label={playing ? "Pause preview of The Way You Love" : "Play preview of The Way You Love"}
                                aria-pressed={playing}
                                className="shrink-0 inline-flex items-center justify-center h-16 w-16 min-h-[56px] min-w-[56px] rounded-full bg-tulip text-garden hover:bg-honeysuckle transition-colors duration-300"
                            >
                                {playing ? <Pause className="h-6 w-6" aria-hidden="true" /> : <Play className="h-6 w-6 ml-1" aria-hidden="true" />}
                            </button>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-honeysuckle">Free preview</p>
                                <p className="font-display font-bold text-lg md:text-xl text-blush leading-tight truncate">“The Way You Love”</p>
                                <div className="flex items-center gap-3 mt-2.5">
                                    <label htmlFor="player-progress" className="sr-only">Seek through the preview</label>
                                    <input
                                        id="player-progress"
                                        type="range"
                                        min="0"
                                        max={duration || 30}
                                        step="0.1"
                                        value={progress}
                                        onChange={seek}
                                        data-testid="player-progress"
                                        className="w-full h-1 cursor-pointer"
                                        style={{ accentColor: "#D17484" }}
                                    />
                                    <span data-testid="player-time" className="text-[11px] md:text-xs tabular-nums text-blush/70 whitespace-nowrap">
                                        {fmt(progress)} / {fmt(duration)}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid="player-mute-btn"
                                onClick={toggleMute}
                                aria-label={muted ? "Unmute preview" : "Mute preview"}
                                aria-pressed={muted}
                                className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full border border-blush/30 text-blush/80 hover:text-tulip hover:border-tulip transition-colors duration-300"
                            >
                                {muted ? <VolumeX className="h-4 w-4" aria-hidden="true" /> : <Volume2 className="h-4 w-4" aria-hidden="true" />}
                            </button>
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                            <button
                                type="button"
                                data-testid="hero-listen-btn"
                                onClick={togglePlay}
                                aria-pressed={playing}
                                className="inline-flex items-center gap-3 bg-rose text-blush px-7 min-h-[56px] text-xs md:text-sm uppercase tracking-[0.18em] font-semibold hover:bg-honeysuckle hover:text-garden transition-colors duration-300"
                            >
                                {playing ? "Pause the preview" : "Listen to The Way You Love"}
                                {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                            </button>
                            <div className="flex items-center gap-5">
                                {[
                                    { label: "Spotify", href: STREAMING_LINKS.spotify, testId: "hero-spotify-link" },
                                    { label: "Apple Music", href: STREAMING_LINKS.apple, testId: "hero-apple-link" },
                                    { label: "YouTube", href: STREAMING_LINKS.youtube, testId: "hero-youtube-link" },
                                ].map((s) => (
                                    <a
                                        key={s.label}
                                        href={s.href}
                                        {...(s.href !== "#" ? { target: "_blank", rel: "noreferrer" } : {})}
                                        data-testid={s.testId}
                                        className="inline-flex items-center gap-1 text-[11px] md:text-xs uppercase tracking-[0.2em] text-blush/70 hover:text-porcelain transition-colors duration-200"
                                    >
                                        {s.label}
                                        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 max-w-xl">
                            <EmailCapture id="hero" variant="dark" />
                        </div>
                    </motion.div>
                </div>

                <div className="relative h-[52vh] lg:h-auto lg:min-h-screen overflow-hidden order-2">
                    <motion.img
                        src="/images/hero.jpg"
                        alt="Mashi J seated in a chair on a wood floor"
                        fetchPriority="high"
                        style={reduce ? undefined : { y: photoY }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </div>
            </div>

            <audio
                ref={audioRef}
                src={AUDIO_PREVIEW_SRC}
                preload="none"
                onPlay={() => { setPlaying(true); setStarted(true); }}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={(e) => setProgress(e.target.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.target.duration || 30)}
            />
        </section>
    );
}
